import React, { useCallback, useEffect, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, ChevronLeft, AlertTriangle, Pause } from 'lucide-react';
interface Episode4NavScreenProps {
  onBack: () => void;
  onNextEpisode: () => void;
}
export function Episode4NavScreen({
  onBack,
  onNextEpisode
}: Episode4NavScreenProps) {
  const [hasWatched, setHasWatched] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPause, setShowPause] = useState(false);
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const handlePlayFullscreen = useCallback(async () => {
    const video = videoRef.current;
    const container = videoContainerRef.current;
    if (!video || !container) return;
    setIsPlaying(true);
    setShowPause(false);
    try {
      if (container.requestFullscreen) {
        await container.requestFullscreen();
      } else if ((container as any).webkitRequestFullscreen) {
        await (container as any).webkitRequestFullscreen();
      }
      await video.play();
    } catch (err) {
      await video.play();
    }
  }, []);
  useEffect(() => {
    if (hasAutoPlayed) return;
    const timer = setTimeout(() => {
      setHasAutoPlayed(true);
      handlePlayFullscreen();
    }, 800);
    return () => clearTimeout(timer);
  }, [hasAutoPlayed, handlePlayFullscreen]);
  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if ((document as any).webkitFullscreenElement) {
      ;
      (document as any).webkitExitFullscreen();
    }
  }, []);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleEnded = () => {
      setIsPlaying(false);
      exitFullscreen();
      setTimeout(() => setShowPause(true), 400);
    };
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && !(document as any).webkitFullscreenElement) {
        if (video && video.ended) {
          setIsPlaying(false);
          setTimeout(() => setShowPause(true), 400);
        }
      }
    };
    video.addEventListener('ended', handleEnded);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      video.removeEventListener('ended', handleEnded);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, [exitFullscreen]);
  const dismissPause = () => {
    setShowPause(false);
    setHasWatched(true);
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    },
    exit: {
      opacity: 0,
      y: -50,
      transition: {
        duration: 0.5
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };
  return <motion.div className="min-h-screen w-full bg-[var(--bg-secondary)] flex flex-col relative overflow-hidden" variants={containerVariants} initial="hidden" animate="visible" exit="exit" data-id="element-133">
      <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] pointer-events-none mix-blend-overlay" data-id="element-134" />

      <div ref={videoContainerRef} className={`${isPlaying && !showPause ? 'block' : 'hidden'} fixed inset-0 z-50 bg-black flex items-center justify-center`} data-id="element-135">
        <video ref={videoRef} className="w-full h-full object-contain bg-black" controls controlsList="nodownload noplaybackrate" disablePictureInPicture onContextMenu={e => e.preventDefault()} playsInline preload="auto" data-id="element-136">
          <source src="https://cdn.shopify.com/videos/c/o/v/65bd6714494d422eb39cb22560f7402a.mp4" type="video/mp4" data-id="element-137" />
        </video>
      </div>

      <AnimatePresence data-id="element-138">
        {showPause && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 1
      }} className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center px-6 text-center" data-id="element-139">
            <motion.div initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          delay: 0.5,
          duration: 0.8
        }} className="w-16 h-0.5 bg-[var(--accent)] mb-10 origin-center" data-id="element-140" />

            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.8
        }} className="flex items-center gap-3 mb-8" data-id="element-141">
              <Pause className="w-6 h-6 text-[var(--accent)]" data-id="element-142" />
              <h2 className="text-5xl md:text-7xl font-serif font-bold tracking-widest text-[var(--text-primary)]" data-id="element-143">
                PAUSA
              </h2>
            </motion.div>

            <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7,
          duration: 0.8
        }} className="text-xl md:text-2xl font-serif text-[var(--text-primary)] leading-relaxed italic mb-10" data-id="element-144">
              ¿Quién está mintiendo o escondiendo algo?
            </motion.p>

            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1,
          duration: 0.8
        }} className="max-w-md space-y-5 mb-12" data-id="element-145">
              <div className="flex items-center gap-4 text-left" data-id="element-146">
                <span className="w-2 h-2 bg-[var(--accent)] rounded-full flex-shrink-0" data-id="element-147" />
                <p className="text-lg md:text-xl font-serif text-[var(--text-primary)]" data-id="element-148">
                  Javier
                </p>
              </div>
              <div className="w-full h-px bg-white/10" data-id="element-149" />
              <div className="flex items-center gap-4 text-left" data-id="element-150">
                <span className="w-2 h-2 bg-[var(--accent)] rounded-full flex-shrink-0" data-id="element-151" />
                <p className="text-lg md:text-xl font-serif text-[var(--text-primary)]" data-id="element-152">
                  Charlie
                </p>
              </div>
              <div className="w-full h-px bg-white/10" data-id="element-153" />
              <div className="flex items-center gap-4 text-left" data-id="element-154">
                <span className="w-2 h-2 bg-[var(--accent)] rounded-full flex-shrink-0" data-id="element-155" />
                <p className="text-lg md:text-xl font-serif text-[var(--text-primary)]" data-id="element-156">
                  Max
                </p>
              </div>
            </motion.div>

            <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1.4,
          duration: 0.8
        }} className="text-[var(--text-secondary)] text-lg font-light tracking-wide mb-16" data-id="element-157">
              Escriban su elección antes de continuar.
            </motion.p>

            <motion.button initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 2,
          duration: 0.6
        }} onClick={dismissPause} className="px-10 py-4 border border-[var(--accent)] text-[var(--text-primary)] uppercase tracking-[0.2em] text-sm transition-all duration-500 hover:bg-[var(--accent-glow)] hover:scale-105" data-id="element-158">
              Continuar
            </motion.button>

            <motion.div initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          delay: 2.2,
          duration: 0.8
        }} className="w-16 h-0.5 bg-[var(--accent)]/30 mt-10 origin-center" data-id="element-159" />
          </motion.div>}
      </AnimatePresence>

      <nav className="w-full p-6 flex justify-between items-center z-30" data-id="element-160">
        <button onClick={onBack} className="flex items-center text-[var(--text-secondary)] hover:text-white transition-colors text-sm tracking-widest uppercase group" data-id="element-161">
          <ChevronLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-162" />
          Volver
        </button>
      </nav>

      <div className="flex-1 flex flex-col justify-center items-center px-6 md:px-12 z-20" data-id="element-163">
        <div className="max-w-4xl w-full mx-auto" data-id="element-164">
          <motion.div variants={itemVariants} className="mb-4" data-id="element-165">
            <span className="text-[var(--text-secondary)] uppercase tracking-[0.2em] text-xs md:text-sm font-medium border-l-2 border-[var(--accent)] pl-4" data-id="element-166">
              Temporada 1 — Caso del Auto Blanco
            </span>
          </motion.div>

          <motion.h2 variants={itemVariants} className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-[var(--text-primary)] mb-8 leading-tight" data-id="element-167">
            EPISODIO 4 — <br className="md:hidden" data-id="element-168" />
            <span className="text-white" data-id="element-169">LA NOCHE DEL 15</span>
          </motion.h2>

          <motion.div variants={itemVariants} className="w-24 h-1 bg-[var(--accent)] mb-10" data-id="element-170" />

          <motion.div variants={itemVariants} className="space-y-4 mb-12 max-w-2xl" data-id="element-171">
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-172">
              Todos recuerdan algo distinto.
            </p>
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-173">
              Y alguien está mintiendo.
            </p>
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-174">
              La última noche de Carmen no fue como parecía.
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="mb-12 max-w-2xl" data-id="element-175">
            <h3 className="text-lg font-serif text-[var(--text-secondary)] uppercase tracking-widest mb-6" data-id="element-176">
              ¿Qué vas a descubrir en este episodio?
            </h3>
            <ul className="space-y-3 text-[var(--text-primary)] font-light" data-id="element-177">
              <li className="flex items-center" data-id="element-178">
                <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3 flex-shrink-0" data-id="element-179" />
                La discusión con Javier
              </li>
              <li className="flex items-center" data-id="element-180">
                <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3 flex-shrink-0" data-id="element-181" />
                Los mensajes insistentes de Charlie
              </li>
              <li className="flex items-center" data-id="element-182">
                <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3 flex-shrink-0" data-id="element-183" />
                Las contradicciones en el círculo cercano
              </li>
            </ul>
          </motion.div>

          <motion.div variants={itemVariants} className="mb-12" data-id="element-184">
            <button onClick={handlePlayFullscreen} className="flex items-center justify-center px-8 py-4 bg-[var(--accent)] text-white font-medium tracking-widest uppercase hover:bg-[var(--accent-hover)] transition-all duration-300 shadow-[0_0_30px_rgba(139,26,26,0.3)] hover:shadow-[0_0_50px_rgba(139,26,26,0.5)] hover:scale-105" data-id="element-185">
              <Play className="w-5 h-5 mr-3 fill-current" data-id="element-186" />
              {hasWatched ? 'Volver a Ver' : 'Ver Episodio 4'}
            </button>
          </motion.div>

          <AnimatePresence data-id="element-187">
            {hasWatched && <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="space-y-12 max-w-3xl" data-id="element-188">
                <div className="w-full h-px bg-white/10" data-id="element-189" />

                <motion.div className="bg-[var(--bg-card)] border-l-4 border-[var(--accent)] p-6 md:p-8 relative overflow-hidden" initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3
            }} data-id="element-190">
                  <div className="absolute top-0 right-0 p-4 opacity-5" data-id="element-191">
                    <AlertTriangle className="w-24 h-24" data-id="element-192" />
                  </div>
                  <div className="relative z-10" data-id="element-193">
                    <div className="flex items-center mb-4 text-[var(--accent)]" data-id="element-194">
                      <AlertTriangle className="w-5 h-5 mr-2" data-id="element-195" />
                      <span className="font-bold tracking-widest uppercase text-sm" data-id="element-196">
                        Importante
                      </span>
                    </div>
                    <p className="text-[var(--text-primary)] text-lg mb-2" data-id="element-197">
                      En este episodio deberán decidir quién está ocultando
                      información.
                    </p>
                  </div>
                </motion.div>

                <motion.div className="flex justify-center pt-4 pb-12" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.6
            }} data-id="element-198">
                  <button onClick={onNextEpisode} className="px-8 py-4 border border-white/10 text-[var(--text-secondary)] uppercase tracking-widest text-sm hover:bg-white/5 hover:text-white hover:border-white/30 transition-all duration-300" data-id="element-199">
                    Continuar al Episodio 5
                  </button>
                </motion.div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>;
}